import api from '@/api/client';

const base = (orgId) => `/organizations/${orgId}/content-wizard`;

export const getConfig = (orgId) => api.get(`${base(orgId)}/config`).then((r) => r.data);
export const listDrafts = (orgId) => api.get(`${base(orgId)}/drafts`).then((r) => r.data);
export const createSession = (orgId) => api.post(`${base(orgId)}/sessions`).then((r) => r.data);
export const getSession = (orgId, sid) => api.get(`${base(orgId)}/sessions/${sid}`).then((r) => r.data);
export const postTurn = (orgId, sid, event) => api.post(`${base(orgId)}/sessions/${sid}/turn`, event).then((r) => r.data);
export const getEditImpact = (orgId, sid, questionId) => api.get(`${base(orgId)}/sessions/${sid}/edit-impact`, { params: { questionId } }).then((r) => r.data);
export const heartbeatLock = (orgId, sid, tabId) => api.post(`${base(orgId)}/sessions/${sid}/lock`, { tabId }).then((r) => r.data);
export const takeOverLock = (orgId, sid, tabId) => api.post(`${base(orgId)}/sessions/${sid}/lock/takeover`, { tabId }).then((r) => r.data);
export const savePreferences = (orgId, sid, chatWidth) => api.patch(`${base(orgId)}/sessions/${sid}/preferences`, { chatWidth }).then((r) => r.data);

export const writeOnePager = (orgId, sid) => api.post(`${base(orgId)}/sessions/${sid}/reports/onepager/write`).then((r) => r.data);
export const downloadReport = (orgId, sid, reportId, format, version) => api
  .get(`${base(orgId)}/sessions/${sid}/reports/${reportId}`, { params: { format, version }, responseType: 'blob' })
  .then((r) => {
    const cd = r.headers['content-disposition'] || '';
    const m = cd.match(/filename\*=UTF-8''([^;]+)/);
    return { blob: r.data, name: m ? decodeURIComponent(m[1]) : `${reportId}.${format}` };
  });
export const uploadSources = (orgId, sid, files) => {
  const form = new FormData();
  Array.from(files).forEach((f) => form.append('files', f));
  return api.post(`${base(orgId)}/sessions/${sid}/sources`, form, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};
export const patchTopics = (orgId, sid, body) => api.patch(`${base(orgId)}/sessions/${sid}/topics`, body).then((r) => r.data);
export const patchBlock = (orgId, sid, blockId, body) => api.patch(`${base(orgId)}/sessions/${sid}/blocks/${blockId}`, body).then((r) => r.data);
export const removeBlock = (orgId, sid, blockId) => api.delete(`${base(orgId)}/sessions/${sid}/blocks/${blockId}`).then((r) => r.data);
export const addBlock = (orgId, sid, topicId, body) => api.post(`${base(orgId)}/sessions/${sid}/topics/${topicId}/blocks`, body).then((r) => r.data);
export const patchSetup = (orgId, sid, body) => api.patch(`${base(orgId)}/sessions/${sid}/setup`, body).then((r) => r.data);
export const reviewItem = (orgId, sid, body) => api.post(`${base(orgId)}/sessions/${sid}/review-items`, body).then((r) => r.data);
export const redraftTopics = (orgId, sid, body) => api.post(`${base(orgId)}/sessions/${sid}/topics/redraft`, body).then((r) => r.data);
export const addLink = (orgId, sid, url) => api.post(`${base(orgId)}/sessions/${sid}/sources/link`, { url }).then((r) => r.data);
export const addLeaveOut = (orgId, sid, items) => api.post(`${base(orgId)}/sessions/${sid}/leave-out`, { items }).then((r) => r.data);
export const relabelSource = (orgId, sid, id, sourceType) => api.patch(`${base(orgId)}/sessions/${sid}/sources/${id}`, { sourceType }).then((r) => r.data);
export const removeSource = (orgId, sid, id) => api.delete(`${base(orgId)}/sessions/${sid}/sources/${id}`).then((r) => r.data);

// Streams one turn (PRD 18.4): message.delta and message.replace update the draft text; turn.completed carries the result.
export const postTurnStream = async (orgId, sid, event, { onDelta, onReplace }) => {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${base(orgId)}/sessions/${sid}/turn`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    body: JSON.stringify(event),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail?.message || body.detail || "I couldn't reply just now. Try again.");
    err.response = { status: res.status, data: body };
    throw err;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let result = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const ev = /^event: (.*)$/m.exec(chunk)?.[1];
      const data = JSON.parse(/^data: (.*)$/m.exec(chunk)?.[1] || 'null');
      if (ev === 'message.delta') onDelta?.(data.text);
      else if (ev === 'message.replace') onReplace?.(data.text);
      else if (ev === 'turn.completed') result = data;
      else if (ev === 'error') { const err = new Error(data.message || 'stale'); err.response = { status: data.status }; err.serverMessage = data.message; throw err; }
    }
  }
  if (!result) throw new Error("I couldn't reply just now. Try again.");
  return result;
};
